import { createSlice } from '@reduxjs/toolkit'
import { signOut } from 'store/auth'
import { FirestoreDatabase } from 'index'
import { Collection, SubjectType } from 'constants/index'
import { toast } from 'react-toastify'
import firebase from 'firebase'
import { deleteBatchDocuments, isValidEmail } from 'utils'
import { chunk, first } from 'lodash'

export const initialState = {
  error: null,
  inProgress: false,
  badgeConfiguration: null,
  badgeConfigurations: [],
  badgeSubject: null,
  badgesSubjectsData: [],
  selectedSubjectsRows: [],
  subjectsCurrentPage: 0,
  subjectsPageSize: 10,
  currentPage: 0,
  pageSize: 10
}

const badgesSlice = createSlice({
  name: 'badges',
  initialState: initialState,
  reducers: {
    setError: (state, action) => {
      state.error = action.payload
    },
    setProgress: (state, action) => {
      state.inProgress = action.payload
    },
    selectedSubjectsRows: (state, action) => {
      state.selectedSubjectsRows = action.payload
    },
    setBadgeSubject: (state, action) => {
      state.badgeSubject = action.payload
    },
    setBadgeConfiguration: (state, action) => {
      state.badgeConfiguration = action.payload
    },
    setBadgeConfigurations: (state, action) => {
      state.badgeConfigurations = action.payload
    },
    setBadgesSubjectsData: (state, action) => {
      state.badgesSubjectsData = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload
    },
    setSubjectsCurrentPage: (state, action) => {
      state.subjectsCurrentPage = action.payload
    },
    setSubjectsPageSize: (state, action) => {
      state.subjectsPageSize = action.payload
    }
  },
  extraReducers: {
    [signOut.fulfilled]: (state) => {
      state.error = null
      state.inProgress = false
      state.badgeConfiguration = null
      state.badgeConfigurations = []
      state.badgesSubjectsData = []
      state.selectedSubjectsRows = []
      state.badgeSubject = null
      state.currentPage = 0
      state.pageSize = 10
    }
  }
})

export const badgeCounterExists = async (counter, conferenceId) => {
  const badgesSnapshot = await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
    .where('conferenceId', '==', conferenceId)
    .where('counter', '==', Number(counter))
    .get()

  return badgesSnapshot?.docs?.length > 0
}

export const getHighestBadgeCounter = async (conferenceId) => {
  try {
    const snapshot = await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
      .where('conferenceId', '==', conferenceId)
      .orderBy('counter', 'desc')
      .limit(1)
      .get()
    const doc = !snapshot.empty ? first(snapshot.docs) : null
    const highestBadge = doc?.exists ? { id: doc.id, ...doc.data() } : null
    return highestBadge
  } catch (err) {
    console.error(err)
  }
}

export const getBadgeSubject = (subjectId) => async (dispatch) => {
  try {
    const docSnap = await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
      .doc(subjectId)
      .get()
    const subject = { id: docSnap.id, ...docSnap.data() }
    dispatch(badgesSlice.actions.setBadgeSubject(subject))
  } catch (err) {
    console.error(err)
  }
}

export const addBadgeSubject = async (payload, conferenceId, onSuccess) => {
  try {
    await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).add({
      ...payload,
      conferenceId: conferenceId
    })
    toast.success(`${payload.Type} Subject added successfully.`)
    onSuccess && onSuccess()
  } catch (err) {
    console.error(err)
    toast.error('Something went wrong while adding the subject.')
  }
}

export const updateBadgeSubject = async (payload, subjectId, conferenceId, onSuccess) => {
  try {
    await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
      .doc(subjectId)
      .update({
        ...payload,
        conferenceId
      })
    toast.success(`${payload.Type} Subject updated successfully.`)
    onSuccess && onSuccess()
  } catch (err) {
    console.error(err)
    toast.error('Something went wrong while updating the subject.')
  }
}

export const getBadgeSubjectsData = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
    .where('conferenceId', '==', conferenceId)
    .orderBy('counter', 'asc')
    .onSnapshot((docs) => {
      const subjects = []
      docs.forEach((doc) => {
        subjects.push({
          id: doc.id,
          ...doc.data()
        })
      })
      callback(subjects)
    })
}

export const deleteBadgeSubject = async (id) => {
  try {
    await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).doc(id).delete()
    toast.success('Subject deleted successfully.')
  } catch (err) {
    console.error(err)
    toast.error('Something went wrong while deleting this subject.')
  }
}

export const deleteMultipleBadgeSubjects = async (deletedSubjects) => {
  try {
    const subjectIds = deletedSubjects.map((s) => s?.id)
    const subjectRefs = subjectIds?.map((id) =>
      FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).doc(id)
    )
    await deleteBatchDocuments(subjectRefs)
    toast.success(`${deletedSubjects?.length} Subject(s) deleted successfully.`)
  } catch (err) {
    console.error(err)
  }
}

export const bulkCreateBadgeSubjectsData = async (data, conferenceId) => {
  try {
    const { attendees, exhibitors, speakers, sponsors } = data

    if (!attendees?.length) {
      toast.warning('No attendees found.')
    }
    if (!exhibitors?.length) {
      toast.warning('No exhibitors found.')
    }
    if (!speakers?.length) {
      toast.warning('No speakers found.')
    }
    if (!sponsors?.length) {
      toast.warning('No sponsors found.')
    }

    const validAttendees = attendees?.filter((doc) => {
      return doc?.Email && isValidEmail(doc?.Email)
    })
    const validExhibitors = exhibitors?.filter((doc) => {
      return doc?.Email && isValidEmail(doc?.Email)
    })
    const validSpeakers = speakers?.filter((doc) => {
      return doc?.Email && isValidEmail(doc?.Email)
    })
    const validSponsors = sponsors?.filter((doc) => {
      return doc?.Email && isValidEmail(doc?.Email)
    })

    const invalidAttendeeEmailsCount = attendees?.length - validAttendees?.length
    const invalidExhibitorEmailsCount = exhibitors?.length - validExhibitors?.length
    const invalidSpeakerEmailsCount = speakers?.length - validSpeakers?.length
    const invalidSponsorEmailsCount = sponsors?.length - validSponsors?.length

    invalidAttendeeEmailsCount > 0 &&
      toast.error(
        `${invalidAttendeeEmailsCount} Attendee(s) have not been imported due to invalid email address`
      )
    invalidExhibitorEmailsCount > 0 &&
      toast.error(
        `${invalidExhibitorEmailsCount} Exhibitor(s) have not been imported due to invalid email address`
      )
    invalidSpeakerEmailsCount > 0 &&
      toast.error(
        `${invalidSpeakerEmailsCount} Speaker(s) have not been imported due to invalid email address`
      )
    invalidSponsorEmailsCount > 0 &&
      toast.error(
        `${invalidSponsorEmailsCount} Sponsor(s) have not been imported due to invalid email address`
      )

    const badgesSnapshot = await FirestoreDatabase.collection(Collection.BADGE_SUBJECTS)
      .where('conferenceId', '==', conferenceId)
      .orderBy('counter', 'desc')
      .get()
    const badgesDocs = badgesSnapshot?.docs ?? []
    const badgeSubjects = badgesDocs?.map((doc) => ({ id: doc?.id, ...doc.data() }))

    const usedCounters = []
    const existingSubjectCounter = {
      Attendee: 0,
      Exhibitor: 0,
      Speaker: 0,
      Sponsor: 0
    }

    const highestCounterInDatabase = first(badgeSubjects)?.counter ?? 0
    let counter = highestCounterInDatabase

    const newBadgeSubjects = []
    const processSubjects = (validSubjects, Type) => {
      return validSubjects.map((e) => {
        const FullName = e?.FullName ?? ''
        const badgeCounter = e?.counter ? parseInt(e?.counter) : null
        if (
          !badgeCounter ||
          usedCounters.includes(badgeCounter) ||
          badgeSubjects?.find((s) => s.counter === badgeCounter)
        ) {
          counter++
          existingSubjectCounter[Type] += 1
          usedCounters.push(counter)
          return { ...e, FullName, counter: counter, Type, conferenceId }
        }
        usedCounters.push(badgeCounter)
        return { ...e, FullName, counter: badgeCounter, Type, conferenceId }
      })
    }

    if (validAttendees?.length) {
      newBadgeSubjects.push(...processSubjects(validAttendees, SubjectType.ATTENDEE))
    }

    if (validExhibitors?.length) {
      newBadgeSubjects.push(...processSubjects(validExhibitors, SubjectType.EXHIBITOR))
    }

    if (validSpeakers?.length) {
      newBadgeSubjects.push(...processSubjects(validSpeakers, SubjectType.SPEAKER))
    }

    if (validSponsors?.length) {
      newBadgeSubjects.push(...processSubjects(validSponsors, SubjectType.SPONSOR))
    }

    const formattedSubjects = newBadgeSubjects.map((item) => {
      if (item['Position/Title']) {
        const sanitizedKey = 'Position/Title'.replace(/[~*\/[\]]/g, '_')
        item[sanitizedKey] = item['Position/Title']
        delete item['Position/Title']
        return item
      }
      return item
    })

    const batchSize = 500 // firebase allows us to commit a batch max 500 documents
    const batchedRecipientsData = chunk(formattedSubjects, batchSize)

    const batchedPromises = batchedRecipientsData.map((batchRecipients) => {
      const batch = FirestoreDatabase.batch()
      batchRecipients.forEach((subject) => {
        const docRef = FirestoreDatabase.collection(Collection.BADGE_SUBJECTS).doc()
        batch.set(docRef, subject)
      })
      return batch.commit()
    })

    await Promise.all(batchedPromises)

    validAttendees?.length &&
      toast.success(`${validAttendees?.length} Attendee(s) imported successfully.`)
    validExhibitors?.length &&
      toast.success(`${validExhibitors?.length} Exhibitor(s) imported successfully.`)
    validSpeakers?.length &&
      toast.success(`${validSpeakers?.length} Speaker(s) imported successfully.`)
    validSponsors?.length &&
      toast.success(`${validSponsors?.length} Sponsor(s) imported successfully.`)

    existingSubjectCounter?.Attendee &&
      toast.warning(
        `${existingSubjectCounter?.Attendee} Attendee badge(s) with existing counters were found, and their counters have been updated to the highest counter +1.`
      )
    existingSubjectCounter?.Exhibitor &&
      toast.warning(
        `${existingSubjectCounter?.Exhibitor} Exhibitor badge(s) with existing counters were found, and their counters have been updated to the highest counter +1.`
      )
    existingSubjectCounter?.Speaker &&
      toast.warning(
        `${existingSubjectCounter?.Speaker} Speaker badge(s) with existing counters were found, and their counters have been updated to the highest counter +1.`
      )
    existingSubjectCounter?.Sponsor &&
      toast.warning(
        `${existingSubjectCounter?.Sponsor} Sponsor badge(s) with existing counters were found, and their counters have been updated to the highest counter +1.`
      )
  } catch (err) {
    toast.error('Unable to import data. Please try again.')
    console.error(err)
  }
}

export const getBadgeConfigurations = (conferenceId, callback) => {
  return FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
    .where('conferenceId', '==', conferenceId)
    .orderBy('createdAt', 'asc')
    .onSnapshot((documents) => {
      const configurations = []
      documents.forEach((doc) => {
        configurations.push({
          id: doc.id,
          ...doc.data()
        })
      })
      const newConfigs = configurations?.map((item, i) => ({ ...item, srNo: i + 1 }))
      callback(newConfigs)
    })
}

export const applicableBadgeConfiguration = async (type, conferenceId) => {
  try {
    const snapshots = await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .where('conferenceId', '==', conferenceId)
      .get()
    const configurations = snapshots.docs.map((e) => ({ id: e.id, ...e.data() }))
    const applicableConfig = configurations?.find((e) => e?.applicableSubjects?.includes(type))
    return applicableConfig ?? null
  } catch (err) {
    console.error(err)
  }
}

export const getBadgeConfiguration = (configurationId) => async (dispatch) => {
  try {
    const docSnap = await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .doc(configurationId)
      .get()
    const configuration = { id: docSnap.id, ...docSnap.data() }
    dispatch(badgesSlice.actions.setBadgeConfiguration(configuration))
  } catch (err) {
    console.error(err)
  }
}

export const updateBadgeConfiguration = async (
  configurationId,
  payload,
  conferenceId,
  onSuccess,
  onError
) => {
  try {
    const { applicableSubjects } = payload

    const results = await checkIfAppliedSubjectExistsInOtherConfiguration({
      isUpdating: true,
      configurationId: configurationId,
      applicableSubjects,
      conferenceId
    })

    if (results?.subjectFound) {
      toast.warning(
        `This configuration cannot be applied to the ${results?.subject} subject(s) because ${results?.subject} already exists in another configuration.`
      )
      onError && onError()
      return
    }

    await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .doc(configurationId)
      .update({
        ...payload,
        conferenceId: conferenceId
      })
    toast.success('Badge print configuration updated successfully.')
    onSuccess && onSuccess()
  } catch (err) {
    console.error(err)
    toast.error('Something went wrong while updating the badge print configuration.')
  }
}

export const deleteBadgeConfiguration = async (configurationId) => {
  try {
    await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
      .doc(configurationId)
      .delete()
    toast.success('Badge print configuration deleted successfully.')
  } catch (err) {
    console.error(err)
    toast.error('Something went wrong while deleting the badge print configuration.')
  }
}

export const addBadgeConfiguration = async (payload, conferenceId, onSuccess, onError) => {
  try {
    const { applicableSubjects } = payload

    const results = await checkIfAppliedSubjectExistsInOtherConfiguration({
      isUpdating: false,
      configurationId: '',
      applicableSubjects,
      conferenceId
    })

    if (results?.subjectFound) {
      toast.warning(
        `This configuration cannot be applied to the ${results?.subject} subject(s) because ${results?.subject} already exists in another configuration.`
      )
      onError && onError()
      return
    }

    await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS).add({
      ...payload,
      conferenceId: conferenceId
    })
    toast.success('Badge print configuration added successfully.')
    onSuccess && onSuccess()
  } catch (err) {
    console.error(err)
    toast.error('Something went wrong while adding the badge print configuration.')
  }
}

export const checkIfAppliedSubjectExistsInOtherConfiguration = async ({
  isUpdating,
  configurationId,
  applicableSubjects,
  conferenceId
}) => {
  const configurationsSnapshot = isUpdating
    ? await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
        .where('conferenceId', '==', conferenceId)
        .where(firebase.firestore.FieldPath.documentId(), '!=', configurationId)
        .get()
    : await FirestoreDatabase.collection(Collection.BADGE_CONFIGURATIONS)
        .where('conferenceId', '==', conferenceId)
        .get()

  let foundSubjects = []
  if (!configurationsSnapshot.empty) {
    const configs = configurationsSnapshot?.docs?.map((doc) => ({ id: doc.id, ...doc.data() }))
    for (let i = 0; i < configs.length; i++) {
      for (let j = 0; j < applicableSubjects.length; j++) {
        if (configs[i]['applicableSubjects'].includes(applicableSubjects[j])) {
          // subject found inside
          foundSubjects.push(applicableSubjects[j])
        }
      }
    }
  }
  return {
    subjectFound: !!foundSubjects?.length,
    subject: foundSubjects?.length ? foundSubjects?.join(', ') : ''
  }
}

export default badgesSlice
